import classNames from 'classnames';
import { bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  OutsideClickHandler,
} from '../../../../components';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import {
  abilityToSell,
  propTypes,
  USER_TYPE_YOUTH
} from '../../../../util/types';


import NotificationDropdown from '../../../../components/NotificationDropdown/NotificationDropdown';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import MenuIcon from '../MenuIcon';
import css from './TopbarDesktop.module.css';
import { cartItemsCount, currentUserRole, getUserPermissions } from '../../../../util/data';
const isActive = typeof window != "undefined" && location && location.pathname === '/p/about';

// {old code for signup route}

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.signupButton}>
      <span className={css.topbarLinkLabels}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

// {join waitlist button redirect to iframe section}
const scrollToSection = () => {
  const targetSection = document.getElementById("iframe-section");
  if (targetSection) {
    targetSection.scrollIntoView({ behavior: "smooth" });
  }
};

// const SignupLink = () => {
//   return (
//     <div
//       name="SignupPage"
//       className={css.signupButton}
//       onClick={scrollToSection}
//     >
//       <span className={css.topbarLinkLabels}>
//         <FormattedMessage id="TopbarDesktop.signup" />
//       </span>
//     </div>
//   );
// };

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.loginButton}>
      <span className={css.topbarLinkLabels}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const AboutusLink = () => {
  return (
    <NamedLink name="CMSPage"
      params={{ pageId: "about" }}
      className={`${css.communityLink} ${isActive ? css.activeLink : ""}`}>
      <FormattedMessage id='TopbarDesktop.AboutUs' />
    </NamedLink>
  );
};
const MarketplaceLink = () => {
  return (
    <NamedLink
      name="SearchPage"
      className={css.communityLink}
      activeClassName={css.activeLink}
    >
      <FormattedMessage id="TopbarDesktop.MarketplaceDropdown" />
    </NamedLink>
  );
};
const AiLandingLink = ({ isAuthenticated }) => {
  return (
    <NamedLink
      name={isAuthenticated ? "RuleBooksSearchPage" : "CMSPage"}
      params={isAuthenticated ? { rulebookId: 'recent' } : { pageId: "ai-landing-page" }}
      className={css.communityLink}
      activeClassName={css.activeLink}>
      <FormattedMessage id="TopbarDesktop.ailandingPage" />
    </NamedLink>
  );
};
const CommunityPageLink = () => {
  return (
    <NamedLink className={css.communityLink} activeClassName={css.activeCommunityLink} name="CircleCommunityPage">Community</NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLinks}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabels}>
        <ProfileIconCard type="cart" />
        {/* <FormattedMessage id="TopbarDesktop.inbox" /> */}
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const CartLink = ({ notificationCount }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationCountCart} >{notificationCount}</div> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="CartPage"
    >
      <span className={css.topbarLinkLabel}>
        <ProfileIconCard type="cart" />
        {/* <FormattedMessage id="TopbarDesktop.cart" /> */}
        {notificationDot}
      </span>
    </NamedLink>
  );
};

export const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  isAuthenticated,
  cartNotificationCount,
  inboxNotificationCount,
  userAbilityToSell
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.profileMenuIsOpen : null;
  };

  const { isSupabaseAdmin } = currentUser?.attributes?.profile?.privateData || {};

  // Define menu items configuration
  const authenticatedMenuItems = [
    {
      key: 'CircleCommunityPage',
      name: 'CircleCommunityPage',
      messageId: 'TopbarDesktop.CommunityDropdown',
    },
    {
      key: 'SearchPage',
      name: 'SearchPage',
      messageId: 'TopbarDesktop.MarketplaceDropdown',
    },
    {
      key: 'ManageListingsPage',
      name: 'ManageListingsPage',
      messageId: 'TopbarDesktop.yourListingsLink',
    },
    {
      key: 'CartPage',
      name: 'CartPage',
      messageId: 'TopbarDesktop.yourCartLink',
      notification: cartNotificationCount > 0 && (
        <div className={css.notificationProfileCartMenu}></div>
      ),
    },
    {
      key: 'InboxPage',
      name: 'InboxPage',
      messageId: 'TopbarDesktop.inbox',
      params: { tab: 'orders' },
      notification: inboxNotificationCount > 0 && (
        <div className={css.notificationProfileInboxMenu}></div>
      ),
    },
    {
      key: 'ProfileSettingsPage',
      name: 'ProfileSettingsPage',
      messageId: 'TopbarDesktop.profileSettingsLink',
    },
    {
      key: 'AccountSettingsPage',
      name: 'AccountSettingsPage',
      messageId: 'TopbarDesktop.accountSettingsLink',
    },
    // Show AdminPanelSupabasePage only for admin users
    ...(isSupabaseAdmin ? [{
      key: 'AdminPanelSupabasePage',
      name: 'AdminPanelSupabasePage',
      messageId: 'TopbarDesktop.adminPanelLink',
    }] : []),
    {
      key: 'StripeSubscriptionPage',
      name: 'StripeSubscriptionPage',
      messageId: 'UserNav.stripeSubscriptionPage',
    },
  ];

  const logoutMenuItem = {
    key: 'logout',
    messageId: 'TopbarDesktop.logout',
    isButton: true,
    onClick: onLogout,
  };

  // Define menu items for non-authenticated users
  const nonAuthenticatedMenuItems = [
    {
      key: 'ManageListingsPage',
      name: 'ManageListingsPage',
      messageId: 'TopbarDesktop.yourListingsLink',
    },
    {
      key: 'StripeSubscriptionPage',
      name: 'StripeSubscriptionPage',
      messageId: 'UserNav.stripeSubscriptionPage',
    },
    {
      key: 'login',
      name: 'LoginPage',
      messageId: 'TopbarDesktop.login',
    },
    {
      key: 'signup',
      name: 'SignupPage',
      messageId: 'TopbarDesktop.signup',
    },
  ];

  const renderMenuItem = (item) => {
    if (item.isButton) {
      return (
        <MenuItem key={item.key}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={item.onClick}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id={item.messageId} />
          </InlineTextButton>
        </MenuItem>
      );
    }

    return (
      <MenuItem key={item.key}>
        <NamedLink
          className={classNames(css.menuLink, currentPageClass(item.name))}
          name={item.name}
          params={item.params}
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id={item.messageId} />
          {item.notification}
        </NamedLink>
      </MenuItem>
    );
  };

  const { publicData, protectedData } = (currentUser && currentUser.id && currentUser.attributes?.profile) || {};
  const { userRole, parentId = false } = publicData || {};
  const { subscriptionData = false } = protectedData || {};

  return (
    <>
      {isAuthenticated ? <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          {isAuthenticated ? <Avatar className={css.avatar} user={currentUser} disableProfileLink /> : <MenuIcon />}
        </MenuLabel>
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key='RuleBooksSearchPage'>
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('RuleBooksSearchPage'))}
              name='RuleBooksSearchPage'
              params={{ rulebookId: 'recent' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.aiPageLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="CircleCommunityPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="CircleCommunityPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.CommunityDropdown" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="SearchPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.MarketplaceDropdown" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ManageListingsPage">
            {userAbilityToSell && <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
            }
          </MenuItem>
          <MenuItem key="CartPage">
            {(userRole !== USER_TYPE_YOUTH && (subscriptionData || !parentId)) && <NamedLink
              className={classNames(css.menuLink, currentPageClass('CartPage'))}
              name="CartPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourCartLink" />
              {typeof cartNotificationCount == "number" && cartNotificationCount > 0 ? <div className={css.notificationProfileCartMenu}></div> : null}
            </NamedLink>}
          </MenuItem>
          <MenuItem key="InboxPage">
            {(userRole !== USER_TYPE_YOUTH && (subscriptionData || !parentId)) && <NamedLink
              className={classNames(css.menuLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ tab: "orders" }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.inbox" />
              {typeof cartNotificationCount == "number" && inboxNotificationCount > 0 ? <div className={css.notificationProfileInboxMenu} ></div> : null}
            </NamedLink>}
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AdminPanelSupabasePage">
            {isSupabaseAdmin && <NamedLink
              className={classNames(css.menuLink, currentPageClass('AdminPanelSupabasePage'))}
              name="AdminPanelSupabasePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.adminPanelLink" />
            </NamedLink>}
          </MenuItem>
          <MenuItem key="StripeSubscriptionPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('StripeSubscriptionPage'))}
              name="StripeSubscriptionPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="UserNav.stripeSubscriptionPage" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
        // :

        //   <Menu>
        //     <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        //       <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        //     </MenuLabel>
        //     <MenuContent className={css.profileMenuContent}>
        //       {authenticatedMenuItems.map(renderMenuItem)}
        //       {renderMenuItem(logoutMenuItem)}
        //     </MenuContent>
        //   </Menu>
        // )
        : (
          <Menu>
            <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
              <MenuIcon />
            </MenuLabel>
            <MenuContent className={css.profileMenuContentLogout}>
              <MenuItem key="CMSPage">
                <NamedLink
                  className={classNames(css.menuLink, currentPageClass('CMSPage'))}
                  name="CMSPage"
                  params={{ pageId: "ai-landing-page" }}
                >
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarDesktop.aiPageLink" />
                </NamedLink>
              </MenuItem>
              <MenuItem key="ManageListingsPage">
                {userAbilityToSell && <NamedLink
                  className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
                  name="ManageListingsPage"
                >
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarDesktop.yourListingsLink" />
                </NamedLink>
                }
              </MenuItem>
              <MenuItem key="StripeSubscriptionPage">
                <NamedLink
                  className={classNames(css.menuLink, currentPageClass('StripeSubscriptionPage'))}
                  name="StripeSubscriptionPage"
                >
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="UserNav.stripeSubscriptionPage" />
                </NamedLink>
              </MenuItem>
              <MenuItem key="login">
                <NamedLink name="LoginPage" className={css.menuLink}>
                  <span className={css.topbarLinkLabels}>
                    <FormattedMessage id="TopbarDesktop.login" />
                  </span>
                </NamedLink>
              </MenuItem>
              <MenuItem key="signup">
                <NamedLink name="SignupPage" className={css.menuLink} >
                  <span className={css.topbarLinkLabels}>
                    <FormattedMessage id="TopbarDesktop.signup" />
                  </span>
                </NamedLink>

              </MenuItem>
            </MenuContent>
          </Menu>
        )}
    </>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    onFetchNotifications,
    onSuccessNotifications,
    isMobileLayout,
    requestNotificationsInProgress,
    notificationsData,
    onFetchNotificationsCount,
    onMarkNotificationRead,
    notificationCountData,
    onResetNotificationsCount,
    onMarkAllNotificationsAsRead,
    onGetNotificationPreference,
    requestNotificationPreferenceInProgress,
    notificaitonPreferenceData,
    onUpdateNotificationPreference
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    onFetchNotificationsCount()
    setMounted(true);
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
    onResetNotificationsCount()
  };
  const [isOpen, setIsOpen] = useState(false);
  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const { cart } = currentUser?.attributes.profile.privateData || {};

  const cartCount = cartItemsCount(cart);

  const { new_notifications_count, new_mentions_count, } = notificationCountData || {};
  const cartLinkMaybe = authenticatedOnClientSide ? (
    <CartLink
      notificationCount={cartCount}
    />
  ) : null;
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;
  const messageLinkMaybe = authenticatedOnClientSide ? (
    <NamedLink className={css.topbarLinks} name="InboxPage" params={{ tab: "orders" }}>
      <ProfileIconCard type="messanger" />
      {notificationCount > 0 ? <div className={css.notificationDotmessage} /> : null}
    </NamedLink>
  ) : null;

  const handleNotificationDropdownOutsideClick = () => {
    if (isMobileLayout) return
    setIsOpen(false);
    onSuccessNotifications({})
  };

  const notificationLinkMaybe = authenticatedOnClientSide ? (
    <OutsideClickHandler onOutsideClick={() => isOpen && handleNotificationDropdownOutsideClick()}>
      <div className={css.notificationWrapper}>
        <div className={css.topbarLinks}
          onClick={handleToggleDropdown}
        >
          {/* show counts from this notificationCountData */}
          <ProfileIconCard type="bell" />
          {(new_notifications_count) ? <div className={css.notificationCount}>{new_notifications_count}</div> : null}
        </div>
        {isOpen ?
          <div className={`${css.notificationDropdown} ${isOpen ? css.open : ''}`}>
            <NotificationDropdown
              requestNotificationPreferenceInProgress={requestNotificationPreferenceInProgress}
              notificaitonPreferenceData={notificaitonPreferenceData}
              onMarkAllNotificationsAsRead={onMarkAllNotificationsAsRead}
              onGetNotificationPreference={onGetNotificationPreference}
              onUpdateNotificationPreference={onUpdateNotificationPreference}
              onFetchNotifications={onFetchNotifications}
              requestNotificationsInProgress={requestNotificationsInProgress}
              notificationsData={notificationsData}
              setIsOpen={() => setIsOpen(false)}
              onMarkNotificationRead={onMarkNotificationRead} />
          </div>
          : null}
      </div>
    </OutsideClickHandler>
  ) : null;

  const userSubscriptionRole = currentUserRole(currentUser);
  const userAbilityToSell = getUserPermissions(userSubscriptionRole, abilityToSell);

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      isAuthenticated={isAuthenticated}
      cartNotificationCount={cartCount}
      inboxNotificationCount={notificationCount}
      userAbilityToSell={userAbilityToSell}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const aboutusMaybe = <AboutusLink />;
  const marketplaceMaybe = <MarketplaceLink />;
  const aiLandingPage = <AiLandingLink isAuthenticated={isAuthenticated}/>;
  const communityPageLink = <CommunityPageLink />

  const { publicData, protectedData } = (currentUser && currentUser.id && currentUser.attributes?.profile) || {};
  const { userRole, parentId = false } = publicData || {};
  const { subscriptionData = false } = protectedData || {};
  const youthAcccess = (userRole !== USER_TYPE_YOUTH && (subscriptionData || !parentId));

  return (
    <nav className={classes}>
      <div className={css.leftSide}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {/* <TopbarSearchForm
          className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
          desktopInputRoot={css.topbarSearchWithLeftPadding}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        /> */}
      </div>

      {/* Community  */}
      <div className={css.rightBox}>
        {/* <CustomLinksMenu
          currentPage={currentPage}
          customLinks={TopbarCommunityDropdown}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          label={intl.formatMessage({ id: 'TopbarDesktop.CommunityDropdown' })}
        /> */}
        {aiLandingPage}
        {communityPageLink}

        {/* MarketPlace  */}
        {marketplaceMaybe}
        {/* <CustomLinksMenu
          currentPage={currentPage}
          customLinks={TopbarMarketPlaceDropdown}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          label={intl.formatMessage({ id: 'TopbarDesktop.MarketplaceDropdown' })}
        /> */}

        {aboutusMaybe}
        {notificationLinkMaybe}
        {youthAcccess ? messageLinkMaybe : null}
        {/* {inboxLinkMaybe} */}
        {youthAcccess ? cartLinkMaybe : null}
        {profileMenuMaybe}
        {loginLinkMaybe}
        {signupLinkMaybe}
      </div>

    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
